import Dialog from '@mui/material/Dialog';
import { isAfter } from 'date-fns';
import { useUpdateUserMetaData } from 'features/user';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { DialogVariantOptions } from 'shared/assets/styles';
import { useToday } from 'shared/contexts/today';
import { PremiumPurchased } from 'shared/dialogs/premium-purchased';
import {
  LoadingPurchasePremium,
  PurchasePremium,
} from 'shared/dialogs/purchase-premium';
import { useRedirectToStripeUrlOrStartTrial } from 'shared/hooks/use-redirect-to-stripe-url-or-start-trial';
import { useToggle } from 'shared/hooks/use-toggle';
import { useUser } from 'shared/hooks/use-user';
import { QueryParamOptions, useQueryParams } from 'shared/lib/react-router-dom';
import { Price, PriceInterval } from 'shared/types/price';
import { getUserHasLifetime } from 'shared/utils/get-user-has-subscription';
import { getUserLastSubscription } from 'shared/utils/get-user-last-subscription';

import {
  PremiumDialogContext,
  PremiumDialogContextProps,
} from './premium-dialog-context';

export type PremiumDialogProviderProps = {
  children: React.ReactNode;
};

export const PremiumDialogProvider: React.FC<PremiumDialogProviderProps> = ({
  children,
}) => {
  const today = useToday();
  const user = useUser();
  const updateUserMetaData = useUpdateUserMetaData();

  const [
    {
      [QueryParamOptions.Premium]: premiumQueryParam,
      [QueryParamOptions.C]: discountCode,
      [QueryParamOptions.Purchase]: purchaseQueryParam,
    },
    setDiscountAndPremiumParams,
  ] = useQueryParams([
    QueryParamOptions.C,
    QueryParamOptions.Purchase,
    QueryParamOptions.Premium,
  ]);
  const [dialogOpen, _, toggleDialogForce] = useToggle(!!discountCode);
  const [initialPriceSelection, setInitialPriceSelection] = useState(
    PriceInterval.Year,
  );
  const { redirectOrStartTrial: redirectToStripeUrl, isLoading } =
    useRedirectToStripeUrlOrStartTrial();

  const onSubmit = async (price?: Price) => {
    if (!price) {
      return;
    }

    const trialStarted = await redirectToStripeUrl({
      interval: price.interval,
    });

    // explicit check on boolean
    if (trialStarted === true) {
      setDiscountAndPremiumParams({ [QueryParamOptions.Premium]: 'true' });
      toggleDialogForce(false);
    }
  };

  const closeDialog = () => {
    toggleDialogForce(false);
    setDiscountAndPremiumParams({});

    // update renewal closed dates
    const lastSubscription = getUserLastSubscription(user);
    const lastRenewalClosedDate =
      user?.user_metadata?.renewDialogClosed?.[
        user?.user_metadata?.renewDialogClosed?.length - 1
      ];
    if (
      !!lastSubscription &&
      lastSubscription.expiresAt &&
      isAfter(today, lastSubscription.expiresAt) &&
      (!lastRenewalClosedDate ||
        isAfter(lastSubscription.expiresAt, lastRenewalClosedDate))
    ) {
      updateUserMetaData({
        renewDialogClosed: [
          ...(user?.user_metadata?.renewDialogClosed ?? []),
          today,
        ],
      });
    }
  };

  const clearPremiumQueryParam = () => setDiscountAndPremiumParams({});

  const value = useMemo<PremiumDialogContextProps>(
    () => ({
      openPremiumDialog: (priceInterval = PriceInterval.Year) => {
        setInitialPriceSelection(priceInterval);
        toggleDialogForce(true);
      },
    }),
    [toggleDialogForce],
  );

  useEffect(() => {
    // user doesnt have premium anymore (so it had premium)
    // and the last subscription does not have a mark for showing that the premium dialog has already been dismissed
    const lastSubscription = getUserLastSubscription(user);
    const lastRenewalClosedDate =
      user?.user_metadata?.renewDialogClosed?.[
        user?.user_metadata?.renewDialogClosed?.length - 1
      ];
    if (
      !!lastSubscription &&
      lastSubscription.expiresAt &&
      isAfter(today, lastSubscription.expiresAt) &&
      (!lastRenewalClosedDate ||
        isAfter(lastSubscription.expiresAt, lastRenewalClosedDate))
    ) {
      // show the dialog
      toggleDialogForce(true);
    }
  }, [today, toggleDialogForce, user]);

  useEffect(() => {
    if (purchaseQueryParam === 'true') {
      // if a user already has lifetime, the user does not need to see a purchase dialog
      if (getUserHasLifetime(user)) {
        setDiscountAndPremiumParams({ [QueryParamOptions.C]: discountCode });
        return;
      }
      // open dialog
      toggleDialogForce(true);
    }
  }, [
    discountCode,
    purchaseQueryParam,
    setDiscountAndPremiumParams,
    toggleDialogForce,
    user,
  ]);

  return (
    <PremiumDialogContext.Provider value={value}>
      {children}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        variant={DialogVariantOptions.Premium}
      >
        <Suspense fallback={<LoadingPurchasePremium onClose={closeDialog} />}>
          <PurchasePremium
            onClose={closeDialog}
            onSubmit={onSubmit}
            isLoading={isLoading}
            initialPriceSelection={initialPriceSelection}
          />
        </Suspense>
      </Dialog>

      <PremiumPurchased
        open={premiumQueryParam === 'true'}
        onClose={clearPremiumQueryParam}
      />
    </PremiumDialogContext.Provider>
  );
};
